/**
 * Icon button.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */
import { MouseEvent, ReactNode } from 'react'
import { IconType } from 'react-icons/lib'
import './IconButton.css'

/** Props for {@link IconButton}. */
export interface IconButtonProps {
  className?: string
  id?: string
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void

  /** Forwarded to the underlying `<button>`'s `name` attribute. */
  name?: string
  /** Forwarded to the underlying `<button>`'s `value` attribute. */
  value?: string | number

  /** An `IconType` component reference or any `ReactNode`. Optional. */
  icon?: IconType | ReactNode
  label?: string
  disabled?: boolean
  children?: any
}

/** A reusable icon button. Pass an `IconType` directly or a pre-rendered node. */
const IconButton = (props: IconButtonProps) => {
  /**
   * `icon` can be an `IconType` (component reference) or a `ReactNode`
   * (pre-rendered element). When it is a function it is treated as an
   * `IconType` and rendered as a component; otherwise it is rendered
   * directly as a node.
   */
  const renderIcon = () => {
    if (typeof props.icon === 'function') {
      // IconType
      const I = props.icon as IconType
      return <I />
    }

    // ReactNode
    return props.icon as ReactNode
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      id={props.id}
      name={props.name}
      value={props.value}
      className={
        props.className ? `icon-button ${props.className}` : 'icon-button'
      }
    >
      {props.icon && <div className="icon">{renderIcon()}</div>}
      {props.label && <div className="label">{props.label}</div>}
      {props.children}
    </button>
  )
}

export default IconButton
